/**
 * Table cell output utilities
 */

import { get as PathGet } from 'lodash';
import moment from 'moment-timezone';

import React from 'react';
import {
  ColumnInterfaceBasedOnValue,
  CellProps,
  HeaderProps
} from 'react-table';
import {
  faTimes,
  faInfo,
  faPhone,
  faEnvelope
} from '@fortawesome/pro-solid-svg-icons';

import Icon from '../../atoms/icon';
import HighlightText from '../../atoms/text/highlight';
import Flag from '../../molecules/flag';
import Tooltip from '../tooltip';
import { Capitalize } from '../../../libs/format';
import { extractRotatingQuestions } from '../../../libs/composer';

export const RESPONSE_FUTURE = (
  <Tooltip content="This session is scheduled for the future. Waiting for a response.">
    <span>--</span>
  </Tooltip>
);
export const RESPONSE_NA = (
  <Tooltip content="Based on previous responses, this data point is not applicable.">
    <span>N/A</span>
  </Tooltip>
);
export const RESPONSE_NONE = (
  <Tooltip content="No response given. Unanswered.">
    <Icon icon={faTimes} />
  </Tooltip>
);

export const NoneGuard = (...values: any[]): JSX.Element => {
  let isNull = true;
  for (const value of values) {
    if (value != null) {
      isNull = false;
      break;
    }
  }
  return isNull ? RESPONSE_NONE : null;
};

export const NoneGuardAll = (...values: any[]): JSX.Element => {
  let isNull = false;
  for (const value of values) {
    if (value == null) {
      isNull = true;
      break;
    }
  }
  return isNull ? RESPONSE_NONE : null;
};

// Type-checking type to ensure T has a K key of type P
type EnsureFieldType<T, K extends keyof T, P> = T[K] extends P ? {} : never;

export type CellFunction<T extends object> =
  ColumnInterfaceBasedOnValue<T>['Cell'];

export type CellGenerator = <
  T extends Record<K, P>,
  K extends keyof T & EnsureFieldType<T, K, P>,
  P
>(
  field: K
) => CellFunction<T>;

type NACheckFunction<T> = (obj: T) => boolean;

export const NAGuard = <T extends object>(
  cell: CellFunction<T>,
  isNA: NACheckFunction<T>
): CellFunction<T> => {
  return (args) => {
    const obj = args.row.original;
    if (isNA(obj)) {
      return RESPONSE_NA;
    }
    return React.createElement(cell as React.ComponentType<CellProps<T>>, args);
  };
};

/**
 * A string field that highlight the specific query parts
 *
 * @param field the field name
 * @param query the query to highlight
 * @param className the highlight class name, optional
 */
export const HighlightCell = <
  T extends Record<K, string>,
  K extends keyof T & EnsureFieldType<T, K, string>
>(
  field: K,
  query: string,
  className?: string
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field] || '';
    query = query || '';
    className = className || 'highlight';
    return <span>{HighlightText(value, query, className)}</span>;
  };
};

/**
 * Direct output field
 *
 * @param field the field name
 */
export const DirectCell = <
  T extends Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  field: K
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field];
    return NoneGuard(value) || <span>{value}</span>;
  };
};

/**
 * Path output field
 *
 * @param path the path to the field
 */
export const PathCell = <
  T extends Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  path: string | string[]
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = PathGet(original, path);
    return NoneGuard(value) || <span>{value}</span>;
  };
};

/**
 * Direct output array of strings
 *
 * @param field the field name
 * @param isCapitalized whether to capitalize each array element
 * @param emptyOutput the output for when the array is empty
 */
export const StringArrayCell = <
  T extends Record<K, string[]>,
  K extends keyof T & EnsureFieldType<T, K, string[]>
>(
  field: K,
  isCapitalized?: boolean,
  emptyOuput?: React.ReactNode
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const values = original[field] as string[];
    if (values == null) {
      return RESPONSE_NONE;
    }
    const output =
      values.length > 0
        ? values
            .map((value) => {
              if (isCapitalized) {
                value = Capitalize(value);
              }
              return value;
            })
            .join(', ')
        : emptyOuput == null
          ? ''
          : emptyOuput;
    return <span>{output}</span>;
  };
};

/**
 * Output a boolean field in Yes/No form
 *
 * @param field the field name
 */
export const YesNoCell = <
  T extends Record<K, boolean>,
  K extends keyof T & EnsureFieldType<T, K, boolean>
>(
  field: K
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field] as boolean;
    return (
      <span>{NoneGuard(value) || <span>{value ? 'Yes' : 'No'}</span>}</span>
    );
  };
};

/**
 * Output a float number with a certain precision
 *
 * @param field the field name
 * @param precision the precision to output the float number in
 */
export const FloatCell = <
  T extends Record<K, number>,
  K extends keyof T & EnsureFieldType<T, K, number>
>(
  field: K,
  precision?: number
): CellFunction<T> => {
  if (precision == null) {
    precision = 2;
  }
  return ({ row: { original } }) => {
    const value = original[field] as number;
    return NoneGuard(value) || <span>{value.toFixed(precision)}</span>;
  };
};

/**
 * Output a time field to a time format respresentation
 *
 * @param field the field name
 * @param form the time output format
 */
export const TimeCell = <
  T extends Record<K, Date>,
  K extends keyof T & EnsureFieldType<T, K, Date>
>(
  field: K,
  format?: string
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field] as Date;
    const time = moment(value);
    format = format || 'MM/DD/YY hh:mmA';
    return NoneGuard(value) || <span>{time.format(format)}</span>;
  };
};

// A shorthand for TimeCell, but outputs a date
export const DateCell = <
  T extends Record<K, Date>,
  K extends keyof T & EnsureFieldType<T, K, Date>
>(
  field: K
): CellFunction<T> => {
  return TimeCell(field, 'MM/DD/YY');
};

/**
 * Output a duration field to a human-friendly format
 *
 * @param field the field name
 */
export const DurationCell = <
  T extends Record<K, number>,
  K extends keyof T & EnsureFieldType<T, K, number>
>(
  field: K
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field] as number;
    if (value == null) {
      return <span>No Pick Up</span>;
    }
    if (value < 60) {
      return <span>{`${value} sec`}</span>;
    }
    const second = value % 60;
    const minute = Math.floor(value / 60);
    let output = `${minute} min`;
    if (second > 0) {
      output += ` ${second} sec`;
    }
    return <span>{output}</span>;
  };
};

/**
 * Output the mapped value of a field
 *
 * @param field the field name
 * @param mapping the field value mapping
 */
export const MappingCell = <
  T extends Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  field: K,
  mapping: Record<T[K], string>
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field] as T[K];
    return NoneGuard(value) || <span>{mapping[value]}</span>;
  };
};

export type FlagConfig = [React.ReactNode, string?];
export type FlagFunc<T> = (obj: T) => FlagConfig | null;

/**
 * Output a series of Flags based on output functions
 *
 * @param obj the base object
 * @param outputs a list of flag output functions
 */
export const GetFlags = <T,>(obj: T, outputs: FlagFunc<T>[]): JSX.Element => {
  const flags: JSX.Element[] = [];
  for (let i = 0; i < outputs.length; i++) {
    const flag = outputs[i](obj);
    if (flag != null) {
      flags.push(
        <Flag className="text-brand-white" key={i * 2} bgColor={flag[1]}>
          {flag[0]}
        </Flag>
      );
      flags.push(<br key={i * 2 + 1} />);
    }
  }
  return <React.Fragment>{flags}</React.Fragment>;
};

/**
 * Direct output field with supplied tootip
 *
 * @param field the field name
 * @param tooltip the react.fragment to display in tooltip.
 */
export const TooltipCell = <
  T extends Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  field: K,
  tooltip: string
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const value = original[field];
    return (
      NoneGuard(value) || (
        <Tooltip id={`tip-${field as string}`} content={<span>{tooltip}</span>}>
          <Icon icon={faInfo} />
          <span className="ml-4">{value}</span>
        </Tooltip>
      )
    );
  };
};

/**
 * Direct output text with supplied tootip
 *
 * @param field the field name
 * @param tooltip the react.fragment to display in tooltip.
 */
export const TooltipHeader = (text: string, tooltip: string) => {
  return (props: HeaderProps<any>) => (
    <Tooltip id={`tip-${text}`} content={<span>{tooltip}</span>}>
      <Icon icon={faInfo} />
      <span className="ml-4">{text}</span>
    </Tooltip>
  );
};

interface WithMethods {
  callId?: number | string;
  callIds?: string | string[];
  messageId?: number | string;
  messageIds?: string | string[];
  schedule?: {
    method: 'call' | 'message';
  };
  type?: 'call' | 'message';
}

/**
 * Output the session method
 */
export const MethodCell = <T extends WithMethods>(): CellFunction<T> => {
  return ({ row: { original } }) => {
    let isCall = false;
    if (original.callId != null || original.callIds?.length > 0) {
      isCall = true;
    } else if (
      original.messageId == null &&
      original.schedule?.method === 'call'
    ) {
      isCall = true;
    }
    return (
      <Tooltip content={<span>{isCall ? 'Phone Call' : 'Text Message'}</span>}>
        <Icon icon={isCall ? faPhone : faEnvelope} />
      </Tooltip>
    );
  };
};

/**
 * Output the survey type
 */
export const SurveyTypeCell = <T extends WithMethods>(): CellFunction<T> => {
  return ({ row: { original: session } }) => {
    const type = session.type;
    return (
      <Tooltip
        content={<span>{type === 'call' ? 'Phone Call' : 'Text Message'}</span>}
      >
        {<Icon icon={type === 'call' ? faPhone : faEnvelope} />}
      </Tooltip>
    );
  };
};

/**
 * A string field that is truncated
 *
 * @param field the field name
 */
export const CommentCell = <
  T extends Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  field: string
): CellFunction<T> => {
  return ({ row: { original: session } }) => {
    const value = session[field];
    if (!value || value.length === 0) {
      return NoneGuard(value);
    }
    if (value.includes('http')) {
      return (
        <audio style={{ width: '280px' }} src={value} controls>
          <track kind="captions" lang="en" label="english_captions" />
        </audio>
      );
    }
    if (value.length < 50) {
      return <span>{value}</span>;
    }
    const firstHalf = value.slice(0, 50).concat('...');
    return <Tooltip content={value}>{firstHalf}</Tooltip>;
  };
};

/**
 * Output the field value for a custom session
 *
 * @param field the field name
 */
export const CustomSessionCell = <
  T extends { _data: Record<string, unknown> } & Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  field: K
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const data = original._data;
    let value = data[field as any];
    if (typeof value === 'boolean') {
      value = value ? 'Yes' : 'No';
    } else if (value === null) {
      value = RESPONSE_NA;
    } else if (value === undefined) {
      value = RESPONSE_NONE;
    }
    return NoneGuard(value) || <span>{value}</span>;
  };
};

/**
 * Output the field value for a rotating question in a custom session
 * Displays question text and response
 *
 * @param field the field name (e.g., 'rotating_message')
 * @param customProgram the schema-based program to extract question texts from
 */
export const RotatingQuestionCell = <
  T extends { _data: Record<string, unknown> } & Record<K, any>,
  K extends keyof T & EnsureFieldType<T, K, any>
>(
  field: K,
  customProgram?: { source: string } | null
): CellFunction<T> => {
  return ({ row: { original } }) => {
    const data = original._data;
    const questionIndex = data.lastRotatingQuestionIndex as number | undefined;
    const responseValue = data[field as any];

    // Format response value
    let formattedResponse: string | React.ReactNode;
    if (typeof responseValue === 'boolean') {
      formattedResponse = responseValue ? 'Yes' : 'No';
    } else if (responseValue === null) {
      formattedResponse = RESPONSE_NA;
    } else if (responseValue === undefined) {
      formattedResponse = RESPONSE_NONE;
    } else {
      formattedResponse = String(responseValue);
    }

    // Try to get question text from schema
    let questionText: string | null = null;
    if (customProgram?.source && questionIndex !== undefined) {
      try {
        const questions = extractRotatingQuestions(customProgram.source);
        if (questions[questionIndex]) {
          questionText = questions[questionIndex].text;
        }
      } catch {
        // Silently fail if extraction fails
      }
    }

    // Format display
    if (questionText) {
      // Truncate long question text for display
      const truncatedText =
        questionText.length > 50
          ? questionText.substring(0, 50) + '...'
          : questionText;
      return (
        <span>
          Q{questionIndex !== undefined ? questionIndex + 1 : '?'}:{' '}
          {truncatedText} - Response: {formattedResponse}
        </span>
      );
    } else {
      // Fallback if question text not available
      return (
        <span>
          {questionIndex !== undefined
            ? `Q${questionIndex + 1}`
            : 'Rotating Question'}
          : {formattedResponse}
        </span>
      );
    }
  };
};
